// ⚠️ FICHIER JUMEAU SDK⇄API — toute modif doit être répliquée À L'IDENTIQUE dans l'autre repo.
//   SDK : src/lib/managers/mail/domain/mailIdentity.ts
//   API : src/functions/domains/mail/shared/mailIdentity.ts
// Réf. conception : Doc/identite-mail/05_conception-detaillee.md §1 (signatures + table de vérité).
//
// RÈGLE ABSOLUE : zéro import repo-spécifique. Fonctions pures uniquement.
// Le type d'entrée est STRUCTUREL (MailCopyRef) — les deux BaseMail le satisfont déjà.
// Toute synchro entre les deux copies se vérifie par diff manuel ; MAILIDENTITY_TWIN_VERSION
// est la sentinelle anti-drift (assertée dans les deux suites de tests jumelles).

/**
 * Marqueur de version du fichier jumeau. DOIT être identique dans les deux repos.
 * Bump à chaque modification du contrat (et réplication immédiate dans l'autre repo).
 */
export const MAILIDENTITY_TWIN_VERSION = "1.2.0";

// ============================================================================
// TYPES
// ============================================================================

/**
 * Vue structurelle minimale d'une copie de mail, suffisante pour l'identité.
 * Les BaseMail du SDK et de l'API satisfont ce type sans adaptation.
 *
 * - `id`                : handle Graph VOLATIL (change au déplacement) — JAMAIS une clé de modèle.
 * - `internetMessageId` : identité du MESSAGE logique (RFC 5322), stable.
 * - `parentFolderId`    : identité du DOSSIER de la copie (Graph: folderId opaque ; Gmail: nom MailFolder).
 */
export type MailCopyRef = {
    id: string;
    internetMessageId: string;
    parentFolderId: string;
};

/**
 * Contexte des dossiers well-known de la boîte courante, fourni par l'appelant.
 * Tous optionnels : ils arrivent parfois tardivement (ex. SDK `inbox-folder-id-updated`).
 * `folderRole` / `pickCanonicalCopy` se comportent en conséquence (cf. note `inboxFolderId` indéfini).
 */
export type MailboxFolderContext = {
    inboxFolderId?: string;
    sentFolderId?: string;
    draftsFolderId?: string;
    deletedFolderId?: string;
};

/**
 * Rôle SÉMANTIQUE d'une copie relativement au MailboxFolderContext.
 *
 * ⚠️ NE PAS CONFONDRE avec l'enum `MailFolder` du provider (noms well-known d'adressage Graph/Google,
 * ex. 'sentitems' / 'deleteditems'). `MailFolder` est volontairement NON importé ici :
 *   1. invariant fichier jumeau (zéro import repo-spécifique) ;
 *   2. les deux enums `MailFolder` divergent déjà entre repos (SDK a `Drafts`, l'API non) ;
 *   3. `FolderRole` porte la valeur `'other'` (catch-all) absente de `MailFolder`, et classe par
 *      RÔLE (comparaison du parentFolderId aux ids du contexte), pas par nom de dossier.
 *
 * Espace de valeurs = les 4 slots de `MailboxFolderContext` + `'other'`. `folderRole` est TOTAL.
 */
export type FolderRole = "inbox" | "sent" | "drafts" | "deleted" | "other";

/**
 * Contrat de retour d'une suppression (figé dès la Phase 1 — interface de stabilité).
 * Réf. Doc/identite-mail/10_suppression-api-deleguee.md §2.
 *
 * Phase 1 : ce delta est CALCULÉ par le SDK après son delete Graph.
 * Phase 2 : ce delta VIENT de la réponse de l'API (DELETE) — même forme.
 * ⇒ le point d'entrée SDK `applyDeletionResult(delta)` ne change pas entre les deux phases ;
 *    seule la SOURCE du delta change. C'est la couture de migration.
 *
 * ✅ Révision 2026-06-08 — cohérence « un seul id » (décision figée #6, Doc 00/09/10 §2) :
 * les copies sont identifiées par `uniqueMailvistaId` (= `internetMessageId::parentFolderId`),
 * et NON plus par le tuple `{ internetMessageId, parentFolderId }`. Le `graphId` reste un
 * handle de mutation OPTIONNEL (jamais une clé) — conservé seulement si un re-ciblage Graph
 * s'avère nécessaire. Le consommateur SDK dérive imid/folderId de `uniqueMailvistaId` au besoin.
 */
export type MailDeletionResult = {
    /** La copie effectivement supprimée. `uniqueMailvistaId` = identité ; `graphId` = handle optionnel. */
    deletedCopy: { uniqueMailvistaId: string; graphId?: string };
    /** Une copie du message survit-elle (dans n'importe quel dossier) ? */
    messageStillExists: boolean;
    /** Les copies restantes du message après suppression. */
    survivingCopies: { uniqueMailvistaId: string; graphId?: string }[];
    /** La copie inbox canonique a-t-elle disparu ? → déclenche le retrait SmartLabel. */
    canonicalInboxRemoved: boolean;
    /** La ligne d'analyse DB a-t-elle été purgée ? */
    dbPurged: boolean;
};

// ============================================================================
// IDENTITÉ
// ============================================================================

/** Clé du MESSAGE logique. */
export function messageKey(m: MailCopyRef): string {
    return m.internetMessageId;
}

/** Clé de la COPIE physique : `${internetMessageId}::${parentFolderId}`. */
export function uniqueMailvistaId(m: MailCopyRef): string {
    return `${m.internetMessageId}::${m.parentFolderId}`;
}

/**
 * Inverse de `uniqueMailvistaId` : décompose un `uniqueMailvistaId` (`internetMessageId::parentFolderId`)
 * en ses deux parts. Utilisé par les consommateurs qui reçoivent un `uniqueMailvistaId` (string) — events
 * `mail-deleted`, routes REST de mutation (bord hexa API), retrait de slot Record — et doivent en dériver
 * l'`internetMessageId` et/ou le `parentFolderId` (décision figée #6 : « un seul id partout »).
 *
 * Split sur le **dernier** `::` : le `parentFolderId` (folderId Graph opaque / nom MailFolder Gmail) ne
 * contient jamais `::`, alors que l'`internetMessageId` (RFC 5322) le pourrait théoriquement. Round-trip
 * garanti : `uniqueMailvistaId(parseUniqueMailvistaId(uid))` === `uid` pour un `uid` bien formé.
 * `uid` sans séparateur (malformé) → `{ internetMessageId: uid, parentFolderId: '' }`.
 */
export function parseUniqueMailvistaId(uid: string): {
    internetMessageId: string;
    parentFolderId: string;
} {
    const sep = uid.lastIndexOf("::");
    if (sep === -1) {
        return { internetMessageId: uid, parentFolderId: "" };
    }
    return {
        internetMessageId: uid.slice(0, sep),
        parentFolderId: uid.slice(sep + 2),
    };
}

/** Deux copies appartiennent-elles au même message logique ? */
export function isSameMessage(a: MailCopyRef, b: MailCopyRef): boolean {
    return messageKey(a) === messageKey(b);
}

/** Deux références désignent-elles la même copie physique ? */
export function isSameCopy(a: MailCopyRef, b: MailCopyRef): boolean {
    return uniqueMailvistaId(a) === uniqueMailvistaId(b);
}

// ============================================================================
// RÔLE DE DOSSIER
// ============================================================================

/**
 * Rôle de la copie d'après son `parentFolderId` et le contexte fourni.
 * Inbox prioritaire en cas de collision d'ids dans le contexte. Total : renvoie `'other'` par défaut.
 * Un slot de contexte indéfini ne peut jamais matcher (pas de faux positif sur `undefined`).
 */
export function folderRole(
    m: MailCopyRef,
    ctx: MailboxFolderContext,
): FolderRole {
    const folderId = m.parentFolderId;
    if (ctx.inboxFolderId !== undefined && folderId === ctx.inboxFolderId)
        return "inbox";
    if (ctx.sentFolderId !== undefined && folderId === ctx.sentFolderId)
        return "sent";
    if (ctx.draftsFolderId !== undefined && folderId === ctx.draftsFolderId)
        return "drafts";
    if (ctx.deletedFolderId !== undefined && folderId === ctx.deletedFolderId)
        return "deleted";
    return "other";
}

/** La copie est-elle dans ce dossier précis ? */
export function isInFolder(m: MailCopyRef, folderId: string): boolean {
    return m.parentFolderId === folderId;
}

/** La copie est-elle la copie inbox (canonique) ? `inboxFolderId` indéfini ⇒ false. */
export function isInboxCopy(
    m: MailCopyRef,
    ctx: MailboxFolderContext,
): boolean {
    return (
        ctx.inboxFolderId !== undefined &&
        m.parentFolderId === ctx.inboxFolderId
    );
}

// ============================================================================
// SÉLECTEURS DE VUE (NON DESTRUCTIFS)
// ============================================================================

/**
 * Vue Dossier : les copies appartenant à `folderId`. Filtre de présentation — ne mute rien.
 */
export function copiesForFolder<T extends MailCopyRef>(
    mails: T[],
    folderId: string,
): T[] {
    return mails.filter((m) => isInFolder(m, folderId));
}

/**
 * Vue SmartLabel (règle canonique STRICTE) : parmi des copies (supposées d'un même message),
 * la copie inbox, sinon `null`.
 *
 * Table de vérité (Doc 05 §1) :
 *   [inbox] → inbox · [inbox, sent] → inbox · [sent] → null · [archive] → null
 *   [deleted] → null · [] → null · inboxFolderId indéfini → null
 *
 * ⚠️ `inboxFolderId` indéfini ⇒ null : c'est volontaire. La RESPONSABILITÉ d'appelant est de ne pas
 * invoquer la réduction canonique tant que le contexte n'est pas prêt (cf. Doc 05 §1 note appelant).
 */
export function pickCanonicalCopy<T extends MailCopyRef>(
    copies: T[],
    ctx: MailboxFolderContext,
): T | null {
    if (ctx.inboxFolderId === undefined) return null;
    return copies.find((c) => c.parentFolderId === ctx.inboxFolderId) ?? null;
}

/**
 * Réduit une liste hétérogène à une copie canonique inbox PAR MESSAGE.
 * Les messages sans copie inbox sont OMIS (exclus des vues inbox-derived). Préserve l'ordre de
 * première apparition de chaque message. Filtre de présentation — ne mute jamais le stockage.
 */
export function dedupToCanonical<T extends MailCopyRef>(
    mails: T[],
    ctx: MailboxFolderContext,
): T[] {
    if (ctx.inboxFolderId === undefined) return [];
    const groups = new Map<string, T[]>();
    const order: string[] = [];
    for (const m of mails) {
        const key = messageKey(m);
        const group = groups.get(key);
        if (group) {
            group.push(m);
        } else {
            groups.set(key, [m]);
            order.push(key);
        }
    }
    const result: T[] = [];
    for (const key of order) {
        const canonical = pickCanonicalCopy(groups.get(key)!, ctx);
        if (canonical !== null) result.push(canonical);
    }
    return result;
}
